import React from 'react';
import styled from 'styled-components';
import { Checkbox, FormControlLabel } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useQuery } from '@apollo/client';
import SuiteOptionsCard from './SuiteOptionsCard';
import RentOptionCard from './RentOptionCard';
import { StyledSubheading } from '../../utils/CommonStyles';
import { IWishlistChoices } from '../../types/worksheet';
import { findDifference, numberWithCommas } from '../../utils/Functions';
import TextField from '@mui/material/TextField';
import { IOption, IProject } from '../../types/project';
import { IUnit } from '../../types/unit';
import { UNITS } from './WishlistChoices';

const SuiteOptions = (props: ChildProps) => {
  const { wishlist, wishlistChoices, notes, setNotes, project, dealOptions, displayPrice } = props;
  const unitTypes = wishlistChoices?.map((w: IWishlistChoices) => w.unitType);

  const { data: unitSizeData } = useQuery(UNITS, {
    skip: !wishlist || !project?._id,
    variables: { projectId: project?._id },
  });
  const availableUnits: IUnit[] = unitSizeData?.getAvailableUnitTypes ?? [];

  return (
    <MainContainer>
      {!wishlist && dealOptions?.length !== 0 && <Subheading>Suite Options</Subheading>}
      <OptionsContainer>
        {wishlist && dealOptions?.length !== 0 && <StyledSubheading>Suite Options</StyledSubheading>}
        <Grid container spacing={2}>
          {wishlist
            ? project?.options &&
              project?.options
                .filter((option: IOption) => option.type === 'residential')
                .map((option: IOption) => {
                  const unitsNotIncluded = findDifference(unitTypes, option.allowedUnits);
                  const allowedSizes = option.allowedUnits?.length
                    ? availableUnits.filter((u: IUnit) => option.allowedUnits!.includes(u.unitType)).map((u: IUnit) => u.size)
                    : [];
                  const smallestAllowedSize = allowedSizes.length ? Math.min(...allowedSizes) : null;
                  // update this is database has option amount limit
                  const amount = 1;

                  return (
                    <React.Fragment key={option.name}>
                      <SuiteOptionsCard
                        title={option.name}
                        price={option.price!}
                        amount={amount}
                        buttonText="Option"
                        errorText={
                          unitsNotIncluded === null || unitsNotIncluded.length === 0
                            ? ''
                            : smallestAllowedSize !== null
                            ? `Limited for Unit Sizes under ${numberWithCommas(smallestAllowedSize)}sf`
                            : `Limited for ${unitsNotIncluded.join(', ')}`
                        }
                        displayPrice={project?.portal.showOptionsPrice ? true : false}
                        displayOptions={project?.portal.showOptions ? true : false}
                      />
                      {option.rent && <RentOptionCard title={option.name} />}
                    </React.Fragment>
                  );
                })
            : project?.options &&
              project?.options
                .filter((option: IOption) => option.type === 'residential')
                .map((option: any) => {
                  const amount = 1;

                  return (
                    <React.Fragment key={option.name}>
                      <SuiteOptionsCard
                        displayOptions={project?.portal.showOptions && option.allowedUnits.includes(props.unitType) ? true : false}
                        displayPrice={project?.portal.showOptionsPrice ? true : false}
                        title={option.name}
                        price={option.price}
                        amount={amount}
                        buttonText="Option"
                      />
                      {option.rent && <RentOptionCard title={option.name} />}
                    </React.Fragment>
                  );
                })}
          {dealOptions &&
            dealOptions.length > 0 &&
            dealOptions.map((option: IOption) => (
              <SuiteOptionsCard
                key={option.name}
                title={option.name}
                purchaseAmount={option.purchaseAmount}
                amount={option.amount}
                type={'deals'}
                displayPrice={displayPrice}
                displayOptions={project?.portal.showOptions ? true : false}
              />
            ))}
        </Grid>
      </OptionsContainer>
      {wishlist && (
        <NotesContainer>
          <TextField
            sx={{
              marginTop: '20px',
              width: '100%',
            }}
            id="outlined-textarea"
            label="Please add any additional inquiries you may have regarding your unit choices"
            placeholder="Enter additional notes about unit choices or price here"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
          />
        </NotesContainer>
      )}
    </MainContainer>
  );
};

interface ChildProps {
  wishlist: boolean;
  project?: IProject;
  dealOptions?: IOption[];
  unitType?: string;
  wishlistChoices?: IWishlistChoices[];
  joinWaitlist?: boolean;
  setJoinWaitlist?: any;
  notes?: string;
  setNotes?: any;
  displayPrice?: boolean;
}

const MainContainer = styled.div`
  @media (max-width: 1024px) {
    display: block;
  }
`;

const OptionsContainer = styled.div`
  @media (max-width: 1024px) {
    display: block;
  }
`;

export const Subheading = styled.div`
  font-size: 1 rem;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 20px;
`;

const NotesContainer = styled.div`
  @media (max-width: 1024px) {
    margin-left: 0 !important;
  }
`;

export default SuiteOptions;
